import { type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ApiErrorText,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  NumberInput,
  SelectInput,
  SelectItem,
  ServiceFeeDisplay,
} from '@/shared/components';
import { getRequiredLabel, useSingleOptionAutoSelect } from '@/shared/lib';
import type { DepositSelectOption } from '@/shared/types';
import type { SccrsDepositFormValues, SccrsGamingHall } from '@/entities/sccrs';
import { GamingHallLabel } from './GamingHallLabel';
import { cn } from '@/shared/utils';

interface Props {
  form: UseFormReturn<SccrsDepositFormValues>;
  handleFormSubmit: (data: SccrsDepositFormValues) => void;
  currencyOptions: DepositSelectOption[];
  platformOptions: SccrsGamingHall[];
  commissionData:
    | {
        feeAmount: number;
        currencyCode: string;
        receiveAmount: number;
      }
    | undefined;
  amountPlaceholder: string;
  shouldShowCommission?: boolean;
  formError?: boolean;
}

export const SccrsForm = ({
  form,
  handleFormSubmit,
  currencyOptions,
  platformOptions,
  commissionData,
  amountPlaceholder,
  shouldShowCommission,
  formError,
}: Props) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form
          onSubmit={event => {
            event.preventDefault();
            form.handleSubmit(handleFormSubmit)(event);
          }}
          className="flex flex-col gap-[1.6rem]">
          <FormField
            control={form.control}
            name="currencyCode"
            render={({ field }) => {
              const label = getRequiredLabel(
                t('depositMethods.sccrs.placeholders.currency'),
                true
              );

              const { isDisabled: controlDisabled } = useSingleOptionAutoSelect(
                currencyOptions,
                field.value,
                field.onChange,
                currencyOptions.length === 0
              );

              return (
                <FormItem>
                  <FormControl>
                    <SelectInput
                      floatingLabel={label}
                      value={field.value}
                      onValueChange={field.onChange}
                      options={currencyOptions}
                      selectedKey="value"
                      labelKey="name"
                      disabled={controlDisabled}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="fls"
            render={({ field }) => {
              const label = getRequiredLabel(
                t('depositMethods.sccrs.placeholders.fls'),
                true
              );

              return (
                <FormItem>
                  <FormControl>
                    <Input label={label} value={field.value} disabled={true} />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="platform"
            render={({ field }) => {
              const label = getRequiredLabel(
                t('depositMethods.sccrs.placeholders.platform'),
                true
              );

              return (
                <FormItem>
                  <FormControl>
                    <SelectInput
                      floatingLabel={label}
                      value={field.value}
                      onValueChange={field.onChange}
                      options={platformOptions}
                      customOptionsRender={o => {
                        const option = o as SccrsGamingHall;
                        return (
                          <SelectItem
                            key={`${option.gamingHallId}_${option.gamingHallName}`}
                            value={option.gamingHallId}
                            showOptionCheckmark={false}
                            className="px-6 py-3 h-auto rounded-lg min-h-[6rem] cursor-pointer">
                            <div className="flex items-center justify-between w-full">
                              <h1 className={'headline4 text-primary'}>
                                <GamingHallLabel hall={option} />
                              </h1>
                            </div>
                          </SelectItem>
                        );
                      }}
                      renderSelected={o => {
                        const option = o as SccrsGamingHall;
                        return (
                          <span
                            className={cn(
                              'p3',
                              'w-full min-w-0 truncate block'
                            )}>
                            <GamingHallLabel hall={option} />
                          </span>
                        );
                      }}
                      valueKey="gamingHallId"
                      disabled={false}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => {
              const placeholder = getRequiredLabel(amountPlaceholder, true);

              return (
                <FormItem>
                  <FormControl>
                    <NumberInput
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      type="text"
                      inputMode="decimal"
                      label={placeholder}
                      isCustomElement
                      className="h-[6rem] rounded-3xl p3"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {shouldShowCommission && commissionData && (
            <ServiceFeeDisplay
              className="mt-[-1.2rem]"
              commissionData={commissionData}
            />
          )}

          {formError ? <ApiErrorText error={formError} /> : null}
        </form>
      </Form>
    </div>
  );
};
